import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form";

import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import Flyout, { FlyoutControlProps } from "../../containers/Flyout";
import { StyledTextInput } from "../Shared/StyledInputs/StyledTextInput";
import { IQRRedirect } from "../../models/qrRedirect/QRRedirect";
import { useQRRedirectMutations } from "../../data/qrRedirect/useQRRedirectMutations";
import { toDataURL } from "../../utils/toDataURL";
import { apiUrl } from "../../services/config";
import { StyledSelectInput } from "../Shared/StyledInputs/StyledSelectInput";
import useUserContext from "../../contexts/UserContext";
import useForms from "../../data/useForms";

interface FormFields extends IQRRedirect {
  customUrl?: string;
}

interface CreateUpdateQRCodeFlyoutProps extends FlyoutControlProps {
  qrCode?: IQRRedirect;
}

const predefinedRoutes = [
  { label: "Website Page - Home", value: "/" },
  { label: "Website Page - Valuation", value: "/valuation" },
  { label: "Website Page - Book a Valuation", value: "/book-a-valuation" },
  { label: "Website Page - Instant Valuation", value: "/instant-valuation" },
  { label: "Website Page - Area Guide", value: "/area-guide" },
  { label: "Website Page - Register", value: "/register" },
  { label: "Website Page - News", value: "/news" },
  { label: "Website Page - Testimonials", value: "/testimonials" },
  { label: "Website Page - Properties", value: "/properties" },
  { label: "Website Page - Properties For Sale", value: "/properties/for-sale" },
  { label: "Website Page - Properties To Rent", value: "/properties/to-rent" },
  { label: "Website Page - Sold Properties", value: "/sold-properties" },
  { label: "Website Page - Let Properties", value: "/let-properties" },
  { label: "Custom", value: "-1" }
]

export function CreateUpdateQRCodeFlyout({ open, setOpen, qrCode }: CreateUpdateQRCodeFlyoutProps) {
  const { create, update } = useQRRedirectMutations();
  const { forms } = useForms();
  const { user: { agency } } = useUserContext();
  const [routes, setRoutes] = useState(predefinedRoutes);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch
  } = useForm<FormFields>({
    defaultValues: { name: "", url: "/", customUrl: "" }
  });
  const _redirectBase = `${apiUrl}/api/redirect/`;
  const _qrScreenSrcBase = `https://api.qrserver.com/v1/create-qr-code/?size=512x512&data=`;
  const _qrPrintSrcBase = `https://api.qrserver.com/v1/create-qr-code/?size=2048x2048&format=svg&data=`;

  const getQRScreenImageSrc = (id: string) => `${_qrScreenSrcBase}${encodeURIComponent(`${_redirectBase}${id}`)}`;

  const getQRPrintImageSrc = (id: string) => `${_qrPrintSrcBase}${encodeURIComponent(`${_redirectBase}${id}`)}`;

  const downloadQRCode = async (type: "screen" | "print") => {
    if (!qrCode) return;

    let dataUrl: string;
    let fileExt = "png";

    if (type === "print") {
      dataUrl = await toDataURL(getQRPrintImageSrc(qrCode.id));
      fileExt = "svg";
    } else {
      dataUrl = await toDataURL(getQRScreenImageSrc(qrCode.id));
    }

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${qrCode.name}-${type}-qr-code.${fileExt}`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const onSubmit = (data: FormFields) => {
    if (data.url === "-1" && data.customUrl) {
      data.url = data.customUrl;
    } else {
      data.url = `${agency.domain}${data.url}`;
    }

    if (qrCode) {
      update.mutate(data, { onSuccess: () => setOpen(false) });
    } else {
      create.mutate(data,
        {
          onSuccess: () => {
            setOpen(false);
            reset({ name: "", url: "/", customUrl: "" })
          }
        });
    }
  }

  useEffect(() => {
    if (!forms.isLoading) {
      const formRoutes = forms.data.map((form: any) => ({
        label: `Landing Page - ${form.title}`,
        value: `/landing/${form.slug}`
      }))

      setRoutes([...formRoutes, ...predefinedRoutes])
    }

    if (qrCode) {
      const urlParts = qrCode.url.split("/");
      const slug = `/${urlParts[urlParts.length - 1]}`;

      const isLanding = urlParts[urlParts.length - 2] === "landing";
      const isCustom = !qrCode.url.startsWith(agency.domain);

      if (isCustom) {
        reset({ ...qrCode, customUrl: qrCode.url, url: "-1" });
      } else {
        reset({ ...qrCode, url: isLanding ? `/landing${slug}` : slug });
      }
    } else {
      reset({ name: "", url: "/", customUrl: "" });
    }
  }, [reset, qrCode, forms.isLoading, forms.data, agency.domain]);

  return (
    <Flyout open={open} setOpen={setOpen} title="Create QR Code Redirect">
      <div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <StyledTextInput
            label="Name"
            register={register}
            registerName="name"
            registerOptions={{ required: "Name is required" }}
          />
          <StyledSelectInput
            label="Page"
            register={register}
            registerName="url"
            options={routes}
            registerOptions={{ required: "Page is required" }}
            setValue={setValue}
            watch={watch}
          />
          {watch("url") === "-1" &&
            <StyledTextInput
              label="URL"
              register={register}
              registerName="customUrl"
            />
          }
          {qrCode &&
            <div className="mx-auto !my-8 p-4 border rounded-md w-1/3 bg-et-pink-100">
              <img src={getQRScreenImageSrc(qrCode.id)} alt={`QR Code for ${qrCode.name}`} />
              <ButtonInput
                isSubmit={false}
                label="Download for Screen"
                onClick={() => downloadQRCode("screen")}
                children={undefined}
                classes="mt-4 !w-full"
              />
              <ButtonInput
                isSubmit={false}
                label="Download for Print"
                onClick={() => downloadQRCode("print")}
                children={undefined}
                classes="mt-4 !w-full"
              />
            </div>
          }
          <div className="mt-6">
            <ButtonInput
              isSubmit={true}
              label={`${qrCode ? "Update" : "Create"} QR Code Redirect`}
              onClick={undefined}
              children={undefined}
              classes={undefined}
            />
          </div>
        </form>
      </div>
    </Flyout>
  )
}